/// /////////////////////////////////////////////////////////////////////////////////////
///                           Copyright (c) 2020 - 2021
///                            Coyote Logistics L.L.C.
///                          All Rights Reserved Worldwide
///
/// WARNING:  This program (or document) is unpublished, proprietary
/// property of Coyote Logistics L.L.C. and is to be maintained in strict confidence.
/// Unauthorized reproduction, distribution or disclosure of this program
/// (or document), or any program (or document) derived from it is
/// prohibited by State and Federal law, and by local law outside of the U.S.
/// //////////////////////////////////////////////////////////////////////////////////////

import axios from "axios";
import types from "../helpers/types";
import qs from "qs";

//For get requests to add the apiVersion to the passed queryString parameters
const getApiVersionParam = (apiVersion) => {
  if (apiVersion == null) {
    return { 'api-version': '1.0' };
  }

  return { 'api-version': apiVersion };
}

//For returning the stringified version of the api-version param to be added to PUT, POST, DELETE, etc actions
const getApiVersionQueryString = (apiVersion) => {
  if (apiVersion == null) {
    return 'api-version=1.0';
  }

  return 'api-version=' + apiVersion;
}

export const API = axios.create({
  baseURL: window.env.GOTRACK_WEB_API_BASE_URL,
});

export const getServiceLevels = async () =>
  Promise.resolve(types.serviceLevels);

//#region GET
//Note: Uses ApiVersionParam function

export const getCoveredRoutesWithMultiFilters = async (inputParams) => {
  var apiParams = { ...inputParams, ...getApiVersionParam('3.0') };

  // The only time we should need to handle an array is when we get "All" route types from the UI
  if(apiParams.routeType == types.routeTypes.all){
    apiParams.routeType = csvToArray(types.legStatuses.ready + "," + types.legStatuses.active + "," + types.legStatuses.complete);
  }
  
  //We need to convert csvs from querystring to an array for posting
  apiParams.carrierId = csvToArray(apiParams.carrierId);
  apiParams.sortType = csvToArray(apiParams.sortType);
  apiParams.serviceLevel = csvToArray(apiParams.serviceLevel);
  apiParams.facilityId = csvToArray(apiParams.facilityId);
  apiParams.regionId = csvToArray(apiParams.regionId);
  apiParams.districtId = csvToArray(apiParams.districtId);

  let payload = qs.stringify(apiParams, { encodeValuesOnly: true, skipNulls: true, arrayFormat: 'repeat', strictNullHandling: true});

  const { data } = await API.get(`api/Routes?${payload}`);
  return data;
};

export const getFacilities = async (searchTerm, apiVersion) => {
  var apiParams = { searchTerm, ...getApiVersionParam(apiVersion) };
  return API.get(`api/Facility/search/`, { params: apiParams }).then(
    ({ data }) => {
      return data;
    }
  );
};

export const getCarrier = async (carrierId, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Carriers/${carrierId}`, { params: apiParams });
  return data;
};

export const getLegs = async (routeId, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Routes/${routeId}/legs`, { params: apiParams });
  return data;
};

export const getLegTrailers = async (legId, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Legs/${legId}/trailers`, { params: apiParams });
  return data;
};

export const getCarriers = async (apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Carriers`, { params: apiParams });
  return data;
};

export const getCarrierManagementList = async (params, apiVersion) => {
  var apiParams = { ...params, ...getApiVersionParam(apiVersion) };
  const { data } = await API.get(`api/Carriers/list`, { params: apiParams });
  return data;
};

export const getSpotLegsWithFilters = async (params, apiVersion) => {
  var apiParams = { ...params, ...getApiVersionParam(apiVersion) };
  const { data } = await API.get(`api/SpotLegs/`, { params: apiParams });
  return data;
};

const csvToArray = (inputCSV) => {
  let outputArray = [];

  if (inputCSV && !(typeof inputCSV === 'string' || inputCSV instanceof String))
  {
    inputCSV = inputCSV.toString();
  }

  if (inputCSV && inputCSV.length > 0) {
    let csvValues = inputCSV.split(',');

    for (const individualValue in csvValues) {
      outputArray.push(csvValues[individualValue]);
    }
  }

  return outputArray;
}

export const getSpotLegsWithMultiFilters = async (inputParams, apiVersion) => {
  var apiParams = { ...inputParams, ...getApiVersionParam(apiVersion) };

  //We need to convert csvs from querystring to an array for posting
  apiParams.destinationState = csvToArray(apiParams.destinationState);
  apiParams.originState = csvToArray(apiParams.originState);
  apiParams.serviceLevel = csvToArray(apiParams.serviceLevel);

  let payload = qs.stringify(apiParams, { encodeValuesOnly: true, skipNulls: true, arrayFormat: 'repeat', strictNullHandling: true});

  const { data } = await API.get(`api/SpotLegs?${payload}`);
  return data;
};

export const getSpotLegsWithSearchTerm = async (params, apiVersion) => {
  var apiParams = { ...params, ...getApiVersionParam(apiVersion) };
  const { data } = await API.get(`api/SpotLegs/search`, { params: apiParams });
  return data;
};

export const getSpotLegById = async (legId, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/SpotLegs/${legId}`, { params: apiParams });
  return data;
};

export const getGoTrackUser = async (apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Users`, { params: apiParams });
  return data;
};

export const getGoTrackUsers = async (searchTerm, apiVersion) => {
  var apiParams = { searchTerm, ...getApiVersionParam(apiVersion) };
  const { data } = await API.get(`api/Users/gospot-search`, { params: apiParams });
  return data;
};

export const getUser = async (userId, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Users/${userId}`, { params: apiParams });
  return data;
};

export const getGoTrackUserPermissions = async (apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Users/permissions`, { params: apiParams });
  return data;
};

export const getUserRecordExists = async (email, apiVersion) => {
  var apiParams = { email, ...getApiVersionParam(apiVersion) };
  const { data } = await API.get(`api/Users/email`, { params: apiParams });
  return data;
};
export const getUserManagementList = async (params, apiVersion) => {
  var apiParams = { ...params, ...getApiVersionParam(apiVersion) };
  const { data } = await API.get(`api/Users/list`, { params: apiParams });
  return data;
};

export const getLocations = async (searchTerm, cancelToken, apiVersion) => {
  try {
    const { data } = await API.get(`api/Locations`, {
      cancelToken,
      params: { searchTerm, ...getApiVersionParam(apiVersion) },
    });

    return data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return [];
    }

    throw error;
  }
};

export const getETA = async (legId, currentLocation, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/locations/${legId}/eta/${currentLocation}`, { params: apiParams });
  return data;
};

export const getNotes = async (legId, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Legs/${legId}/notes`, { params: apiParams });
  return data;
};

export const getLatestNoteForLeg = async (legId, cancelToken, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  try {
    const response = await API.get(
      `api/Legs/${legId}/notes/latest`,
      {
        cancelToken,
        params: apiParams
      });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return '';
    }
    throw error;
  }
}

export const getLatestNoteForSpotLeg = async (spotLegId, cancelToken, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  try {
    const response = await API.get(
      `api/SpotLegs/${spotLegId}/notes/latest`,
      {
        cancelToken,
        params: apiParams
      });
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return '';
    }
    throw error;
  }
}

export const getSpotNotes = async (spotLegId, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/SpotLegs/${spotLegId}/notes`, { params: apiParams });
  return data;
};

export const getFeatureFlag = async (featureFlagName, apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(
    `api/FeatureFlags?featureFlagName=${featureFlagName}`,
    { params: apiParams }
  );
  return data;
};

export const getSpotLegsCsv = async (departureDateStart, departureDateEnd, apiVersion) => {
  var apiParams = { departureDateStart, departureDateEnd, ...getApiVersionParam(apiVersion) };
  return API.get(`api/SpotLegs/csv-export`, {
    responseType: "blob",
    params: apiParams,
  });
}

export const getOrganizations = async (apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Organizations`, { params: apiParams });
  return data;
};

export const getTierSettings = async (apiVersion) => {
  var apiParams = getApiVersionParam(apiVersion);
  const { data } = await API.get(`api/Tiers`, { params: apiParams });
  return data;
};

//#endregion

//#region PUT
//Note: Uses ApiVersionQueryString function

export const putCarrierUpdate = async (carrierId, updates, apiVersion) => {
  return await API.put(`api/Carriers/${carrierId}?${getApiVersionQueryString(apiVersion)}`, updates);
}

export const putCarrier = async (carrier, apiVersion) => {
  return await API.put(`api/Carriers?${getApiVersionQueryString(apiVersion)}`, carrier);
};

export const putUserUpdate = async (userId, updates, apiVersion) => {
  return API.put(`/api/Users/${userId}?${getApiVersionQueryString(apiVersion)}`, updates);
}

export const putUsersAssociatedCarriers = async (userId, carrierIds, apiVersion) => {
  return await API.put(`/api/Users/${userId}/carriers?${getApiVersionQueryString(apiVersion)}`, { carrierIds });
}

export const putLegDeparture = (legId, departRequest, apiVersion) => {
  return API.put(`api/Legs/${legId}/depart?${getApiVersionQueryString(apiVersion)}`, departRequest);
}

export const putLegUpdate = (legId, updates, apiVersion) => {
  return API.put(`api/Legs/${legId}?${getApiVersionQueryString(apiVersion)}`, updates);
}

export const putSpotLegUpdate = (updates, apiVersion) => {
  return API.put(`api/SpotLegs?${getApiVersionQueryString(apiVersion)}`, updates);
}

export const deleteAssignedUser = (spotLegId, apiVersion) => {
  return API.put(`api/SpotLegs/${spotLegId}/remove-assigned-user?${getApiVersionQueryString(apiVersion)}`); //TODO: change backend to DELETE
}

export const putSpotNotesRead = async (spotLegId, noteIds, apiVersion) => {
  return await API.put(`api/SpotLegs/${spotLegId}/notes?${getApiVersionQueryString(apiVersion)}`, noteIds);
}

export const putLastActiveDate = async (apiVersion) => {
  return await API.put(`api/Users/last-active-date?${getApiVersionQueryString(apiVersion)}`);
}

export const putManageDelaySettings = async (
  tierId,
  delayInMinutes,
  userId,
  updateDate,
  apiVersion
) => {
  return await API.put(
    `api/Tiers/${tierId}?delayInMinutes=${delayInMinutes}&userId=${userId}&updateDate=${updateDate}&${getApiVersionQueryString(apiVersion)}`
  );
};

//#endregion

//#region POST
//Note: Uses ApiVersionQueryString function

export const createNewUser = async (user, apiVersion) => {
  return API.post(`/api/Users?${getApiVersionQueryString(apiVersion)}`, user);
}

export const postWatch = (legId, apiVersion) => {
  return API.post(`api/Legs/${legId}/watch?${getApiVersionQueryString(apiVersion)}`);
}

export const postSpotLegs = async (body, apiVersion) => {
  const { data } = await API.post(`api/SpotLegs?${getApiVersionQueryString(apiVersion)}`, body);
  return data;
};

export const postSpotLegNote = (spotLegId, body, apiVersion) => {
  return API.post(`api/SpotLegs/${spotLegId}/notes?${getApiVersionQueryString(apiVersion)}`, body);
}

export const postSpotLegClaimed = (spotLegId, apiVersion) => {
  return API.post(`api/SpotLegs/${spotLegId}/Claim?${getApiVersionQueryString(apiVersion)}`);
}

export const postCarrierSpotLegClaim = (spotLegId, claimDetails, apiVersion) => {
  return API.post(`api/SpotLegClaims/${spotLegId}/Claim?${getApiVersionQueryString(apiVersion)}`, claimDetails);
}

export const postProcessSpotLegClaim = (claimsId, params, apiVersion) => {
  if (params.accepted) {
    return API.post(
      `api/SpotLegClaims/${claimsId}/process?accepted=${params.accepted}&${getApiVersionQueryString(apiVersion)}`
    );
  } else {
    return API.post(
      `api/SpotLegClaims/${claimsId}/process?accepted=${params.accepted}&rejectionReason=${params.rejectionReason}&rejectionNote=${params.rejectionNote}&${getApiVersionQueryString(apiVersion)}`
    );
  }
};

export const postRemoveSpotLegClaim = (spotLegId, apiVersion) => {
  return API.post(`api/SpotLegClaims/${spotLegId}/cancel-claim?${getApiVersionQueryString(apiVersion)}`);
}

export const postAssignSpotLegWithoutClaim = (spotLegId, carrierId, apiVersion) => {
  return API.post(
    `api/SpotLegClaims/${spotLegId}/assign-carrier?assignedCarrierId=${carrierId}&${getApiVersionQueryString(apiVersion)}`
  );
}

export const postUserInvite = async (userId, apiVersion) => {
  return await API.post(`api/Users/${userId}/invite?${getApiVersionQueryString(apiVersion)}`);
}

export const postOrganization = async (name, apiVersion) => {
  return await API.post(`api/Organizations?${getApiVersionQueryString(apiVersion)}`, { name });
}

//#endregion

//#region DELETE
//Note: Uses ApiVersionQueryString function

export const deleteWatch = (legId, apiVersion) => {
  return API.delete(`api/Legs/${legId}/watch?${getApiVersionQueryString(apiVersion)}`);
}

//#endregion

//#region PATCH
//Note: Uses ApiVersionQueryString function

export const patchNotesRead = async (legId, noteId, apiVersion) => {
  return await API.patch(`api/Legs/${legId}/notes/${noteId}?${getApiVersionQueryString(apiVersion)}`);
}

//#endregion
